import React from 'react';
import {
  Box, Typography, SxProps, Theme,
  useTheme,
} from '@mui/material';
import DiscountIcon from '@mui/icons-material/Discount';
import ScheduleIcon from '@mui/icons-material/Schedule';
import type { UserCoupon } from '../../queries/coupons';
import { getMisCuponesStyles } from './style';
import AlertDialog from '../AlertDialog';
import CouponDetailContent, { couponDiscountHeadline } from './CouponDetailContent';

function formatDiscount(coupon: UserCoupon): string {
  if (coupon.discountType === 'PERCENTAGE') {
    return `${coupon.discountValue}% de descuento`;
  }
  return `$${coupon.discountValue.toLocaleString('es-CL')} de descuento`;
}

function formatCondition(coupon: UserCoupon): string | null {
  if (coupon.minPurchaseAmount && coupon.minPurchaseAmount > 0) {
    return `En compras sobre $${coupon.minPurchaseAmount.toLocaleString('es-CL')}`;
  }
  return null;
}

function formatExpiry(iso: string | null): string | null {
  if (!iso) {
    return null;
  }
  const date = new Date(iso);
  if (Number.isNaN(date.getTime())) {
    return null;
  }
  return `Vence ${date.toLocaleDateString('es-CL', { day: 'numeric', month: 'short', year: 'numeric' })}`;
}

function CouponTicket({ coupon, onClick }: { coupon: UserCoupon; onClick: () => void }) {
  const styles = getMisCuponesStyles(useTheme());
  const condition = formatCondition(coupon);
  const expiry = formatExpiry(coupon.expiresAt);
  const isReserved = coupon.status === 'RESERVED';
  return (
    <Box
      onClick={onClick}
      sx={{ ...styles.ticket, cursor: 'pointer', ...(isReserved ? { opacity: 0.72 } : {}) }}
    >
      <Box sx={styles.ticketIconCol}>
        <DiscountIcon aria-hidden sx={styles.ticketIcon} />
      </Box>
      <Box sx={styles.ticketBody}>
        <Typography variant="body2" fontWeight="bold" color="primary">
          {formatDiscount(coupon)}
        </Typography>
        {condition && (
          <Typography variant="caption" color="text.secondary">
            {condition}
          </Typography>
        )}
        <Box sx={styles.ticketMeta}>
          <Box component="span" sx={styles.ticketCode}>
            {coupon.code.substring(0, 20)}
          </Box>
          {isReserved ? (
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.25 }}>
              <ScheduleIcon aria-hidden sx={{ fontSize: 14, color: 'warning.main' }} />
              <Typography variant="caption" color="text.secondary">En uso</Typography>
            </Box>
          ) : expiry && (
            <Typography variant="caption" color="text.secondary">
              {expiry}
            </Typography>
          )}
        </Box>
      </Box>
    </Box>
  );
}

type MisCuponesCardProps = {
  coupons: UserCoupon[] | null;
  sxProps?: SxProps<Theme>;
};

export default function MisCuponesCard({ coupons, sxProps }: MisCuponesCardProps) {
  const styles = getMisCuponesStyles(useTheme());
  const [selected, setSelected] = React.useState<UserCoupon | null>(null);
  if (!coupons || coupons.length === 0) {
    return null;
  }
  const onCloseAction = () => {
    setTimeout(() => setSelected(null), 100);
  };

  return (
    <Box sx={{ ...styles.card, ...sxProps }}>
      <Box sx={styles.header}>
        <DiscountIcon aria-hidden fontSize="small" sx={styles.headerIcon} />
        <Typography variant="h6" fontWeight="bold" color="primary">
          Mis cupones
        </Typography>
        <Typography
          component="span"
          variant="caption"
          aria-label={`${coupons.length} cupones disponibles`}
          sx={styles.countChip}
        >
          {coupons.length}
        </Typography>
      </Box>
      <Box sx={styles.scrollArea}>
        <Box sx={styles.scrollRow}>
          {coupons.map((coupon) => (
            <CouponTicket
              key={`${coupon.code}-${coupon.expiresAt ?? 'na'}`}
              coupon={coupon}
              onClick={() => setSelected(coupon)}
            />
          ))}
        </Box>
        {coupons.length > 1 && (
          <Box aria-hidden sx={styles.fadeRight} />
        )}
      </Box>
      <AlertDialog
        isTriggered={!!selected}
        onCloseAction={onCloseAction}
        hideSubmitButton
        hideCloseButton="corner"
        maxWidth="xs"
        dialogTitle={selected ? (
          <Typography component="div" variant="h6" fontWeight="bold" textAlign="center" color="secondary">
            {couponDiscountHeadline(selected)}
          </Typography>
        ) : ''}
        dialogContent={selected ? <CouponDetailContent coupon={selected} /> : ''}
      />
    </Box>
  );
}

MisCuponesCard.defaultProps = {
  sxProps: undefined,
};
